import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { DotNetComponent } from '../typings/global';

interface WindowWithAdvancedMarker extends Window {
	google?: {
		maps?: {
			marker?: {
				AdvancedMarkerElement?: new (options: Record<string, unknown>) => unknown;
			};
		};
	};
}

export interface MapExtendedOptions {
	mapId?: string;
	zoom?: number;
	maxZoom?: number;
	draggable?: boolean;
	gestureHandling?: google.maps.GestureHandlingOptions;
	defaultCenter?: google.maps.LatLng;
	mapStyles?: google.maps.MapTypeStyle[];
	styledMapName?: string;
	mapTypeControlStyle?: google.maps.MapTypeControlStyle;
}

export class MapComponent {
	static CustomMarker: unknown;
	private _options: MapExtendedOptions = {};
	private map!: google.maps.Map;
	private markers: Array<google.maps.Marker | unknown> = []; // Using google.maps.marker.AdvancedMarkerElement (recommended over deprecated Marker)
	private items: HTMLElement[] = [];
	private markerClusterer: MarkerClusterer | null = null; // MarkerClusterer instance

	constructor(private element: HTMLElement, private dotNetReference: DotNetComponent | null) {
		this.initMap();
		this.addMarkers();
		this.addCircles();
		this.initClusterMarkers();
	}

	private initMap() {
		this.items = Array.from(
			this.element.querySelectorAll('.js-map-marker')
		) as HTMLElement[];

		const centerLng = parseFloat(
			this.element.getAttribute('data-center-lng') || '9'
		);
		const centerLat = parseFloat(
			this.element.getAttribute('data-center-lat') || '40'
		);
		const zoom = parseInt(this.element.getAttribute('data-zoom') || '9');

		console.log('Center: ' + centerLng + ',' + centerLat);

		this._options = {
			mapId: '6ac974bc3b1e377f4171eb26 ',
			zoom: zoom,
			maxZoom: 14,
			defaultCenter: new google.maps.LatLng(centerLat, centerLng),
			draggable: false,
			gestureHandling: 'greedy' as google.maps.GestureHandlingOptions,
			styledMapName: 'Styled',
			mapTypeControlStyle: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
		};

		const mapOptions: google.maps.MapOptions = {
			//mapId: this._options.mapId,
			zoom: this._options.zoom,
			maxZoom: this._options.maxZoom,
			center: this._options.defaultCenter,
			disableDefaultUI: true,
			gestureHandling: this._options.gestureHandling,
			mapTypeControlOptions: {
				style: this._options.mapTypeControlStyle,
			},
			styles: [
				{
					featureType: 'administrative',
					elementType: 'labels.text.fill',
					stylers: [{ color: '#444444' }],
				},
				{
					featureType: 'landscape',
					elementType: 'all',
					stylers: [{ color: '#f2f2f2' }],
				},
				{
					featureType: 'poi',
					elementType: 'all',
					stylers: [{ visibility: 'off' }],
				},
				{
					featureType: 'road',
					elementType: 'all',
					stylers: [{ saturation: -100 }, { lightness: 45 }],
				},
				{
					featureType: 'road.highway',
					elementType: 'all',
					stylers: [{ visibility: 'simplified' }],
				},
				{
					featureType: 'road.arterial',
					elementType: 'labels.icon',
					stylers: [{ visibility: 'off' }],
				},
				{
					featureType: 'transit',
					elementType: 'all',
					stylers: [{ visibility: 'off' }],
				},
				{
					featureType: 'water',
					elementType: 'all',
					stylers: [{ color: '#4682b4' }, { visibility: 'on' }],
				},
			],
		};

		this.map = new google.maps.Map(this.element, mapOptions);
	}

	private addMarkers(): void {
		// Exit early if no items
		if (!this.items || this.items.length === 0) return;

		this.items.forEach((item) => {
			const isDisabled = item.getAttribute('data-disabled') !== null;
			const isOnMerkliste = item.getAttribute('data-is-on-merkliste') !== null;
			const title = item.getAttribute('data-title') ?? ''; // Default to empty string if null

			// Parse lat/lng safely
			const lat = parseFloat(item.dataset.lat || 'NaN');
			const lng = parseFloat(item.dataset.lng || 'NaN');

			// Validate coordinates
			if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
				console.warn(`Skipping marker: Invalid coordinates for item`, item);
				return;
			}

			const position = new google.maps.LatLng(lat, lng);
			
			// Use AdvancedMarkerElement (recommended over deprecated Marker)
			// Create SVG content for the marker
			const markerContent = this.createMarkerContent(isOnMerkliste, isDisabled);
			
			const AdvancedMarkerElement = (window as WindowWithAdvancedMarker).google?.maps?.marker?.AdvancedMarkerElement;
			if (!AdvancedMarkerElement) {
				console.warn('AdvancedMarkerElement not available, falling back to Marker');
				const marker = new google.maps.Marker({
					position,
					icon: this.getSvgMarker(isOnMerkliste, isDisabled),
					map: this.map,
				});
				marker.addListener('click', () => {
					this.dotNetReference?.invokeMethodAsync(
						'MapAssemblyMarkerClick',
						title
					);
				});
				this.markers.push(marker);
				return;
			}

			const marker = new AdvancedMarkerElement({
				position,
				map: this.map,
				content: markerContent,
				title: title,
			}) as unknown;

			// Add click listener (works with both Marker and AdvancedMarkerElement)
			if (marker && typeof marker === 'object') {
				const m = marker as Record<string, unknown>;
				if (typeof m.addListener === 'function') {
					(m.addListener as (event: string, callback: () => void) => void)('click', () => {
						this.dotNetReference?.invokeMethodAsync(
							'MapAssemblyMarkerClick',
							title
						);
					});
				}
			}

			this.markers.push(marker);
		});
	}

	/**
	 * Create SVG marker content for AdvancedMarkerElement
	 */
	private createMarkerContent(isOnMerkliste: boolean, isDisabled: boolean): HTMLElement {
		const color = isOnMerkliste ? 'yellow' : isDisabled ? '#CFCFCF' : '#96bf32';
		const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
		svg.setAttribute('viewBox', '0 0 48 64');
		svg.setAttribute('width', '32');
		svg.setAttribute('height', '48');
		svg.setAttribute('fill', color);
		svg.setAttribute('stroke', '#000');
		svg.setAttribute('stroke-width', '2');
		
		const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
		path.setAttribute('d', 'M23.2,64.6C5.2,38.2,1.8,35.5,1.8,25.7c0.1-13.3,10.8-24.1,24-24 c13.1,0.1,23.7,10.8,23.7,24c0,9.7-3.4,12.4-21.4,38.9c-0.9,1.4-2.8,1.7-4.2,0.8C23.7,65.2,23.4,64.9,23.2,64.6z M25.7,35.8 c5.5,0,10-4.5,10-10.1s-4.5-10.1-10-10.1s-10,4.5-10,10C15.7,31.3,20.2,35.8,25.7,35.8L25.7,35.8z');
		
		svg.appendChild(path);
		return svg as unknown as HTMLElement;
	}

	private getSvgMarker(isOnMerkliste: boolean, isDisabled: boolean) {
		return {
			path: 'M23.2,64.6C5.2,38.2,1.8,35.5,1.8,25.7c0.1-13.3,10.8-24.1,24-24 c13.1,0.1,23.7,10.8,23.7,24c0,9.7-3.4,12.4-21.4,38.9c-0.9,1.4-2.8,1.7-4.2,0.8C23.7,65.2,23.4,64.9,23.2,64.6z M25.7,35.8 c5.5,0,10-4.5,10-10.1s-4.5-10.1-10-10.1s-10,4.5-10,10C15.7,31.3,20.2,35.8,25.7,35.8L25.7,35.8z',
			fillColor: isOnMerkliste ? 'yellow' : isDisabled ? '#CFCFCF' : '#96bf32',
			fillOpacity: 1,
			strokeWeight: 2,
			rotation: 0,
			scale: 0.5,
			anchor: new google.maps.Point(13, 33),
		};
	}

	private addCircles() {
		const searchRadius = parseFloat(
			this.element.getAttribute('data-search-radius') || '0'
		);

		const scs = this.element.getAttribute('data-search-centers') || '';
		if (scs === '') {
			return;
		}

		const searchCenterPairs = scs.split('-');
		const coords = searchCenterPairs.map((pair) => {
			const [lat, lng] = pair
				.split(',')
				.map((coord) => parseFloat(coord.trim()));
			return { lat, lng };
		});

		coords.forEach((coord) => {
			console.log(
				`Latitude: ${coord.lat}, Longitude: ${coord.lng}, Radius: ${searchRadius},`
			);
			this.addCircle(coord.lat, coord.lng, searchRadius);
		});
	}

	private addCircle(
		searchCenterLat: number,
		searchCenterLng: number,
		searchRadius: number
	) {
		if (!Number.isNaN(searchRadius) && searchRadius < 101) {
			const center = new google.maps.LatLng(searchCenterLat, searchCenterLng);
			console.log(center);

			new google.maps.Circle({
				strokeColor: '#54AF38',
				strokeOpacity: 0.8,
				strokeWeight: 2,
				fillColor: '#54AF38',
				fillOpacity: 0.35,
				map: this.map,
				center: center,
				radius: searchRadius * 1000,
			});
		}
	}

	/**
	 * Initialize marker clustering.
	 * Uses @googlemaps/markerclusterer library.
	 * Disables clustering at max zoom level.
	 */
	private initClusterMarkers(): void {
		if (!this.markers || this.markers.length < 2) {
			console.log('Not enough markers to cluster (need at least 2)');
			return;
		}

		try {
			// Initialize MarkerClusterer with custom green color (#94c119 from --green-dark)
			this.markerClusterer = new MarkerClusterer({
				map: this.map,
				markers: this.markers,
				renderer: {
					render: ({ count, position }) => {
						// Create custom cluster marker with green color
						const color = '#94c119'; // --green-dark from variables.scss
						
						const svg = `
							<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="50" height="50">
								<circle cx="120" cy="120" opacity="0.6" r="70" />
								<circle cx="120" cy="120" opacity="0.3" r="90" />
								<circle cx="120" cy="120" opacity="0.2" r="110" />
								<text x="50%" y="50%" style="fill:#fff" text-anchor="middle" font-size="50" dominant-baseline="middle" font-family="roboto,arial,sans-serif" font-weight="bold">
									${count}
								</text>
							</svg>
						`;
						
						// Create cluster marker element
						const parser = new DOMParser();
						const svgEl = parser.parseFromString(svg, 'image/svg+xml').documentElement;
						svgEl.setAttribute('transform', 'translate(0 25)');
						
						const clusterOptions = {
							position,
							zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
						};

						// Check if AdvancedMarkerElement is available
						const AdvancedMarkerElement = (window as WindowWithAdvancedMarker).google?.maps?.marker?.AdvancedMarkerElement;
						
						if (AdvancedMarkerElement) {
							return new AdvancedMarkerElement({
								...clusterOptions,
								content: svgEl,
							});
						} else {
							// Fallback to regular Marker with icon
							return new google.maps.Marker({
								...clusterOptions,
								icon: {
									url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
									scaledSize: new google.maps.Size(50, 50),
								},
								label: {
									text: String(count),
									color: '#fff',
									fontSize: '14px',
									fontWeight: 'bold',
								},
							});
						}
					},
				},
			});

			// Listen to zoom changes and disable clustering at max zoom
			this.map.addListener('zoom_changed', () => {
				const currentZoom = this.map.getZoom();
				const maxZoom = this._options.maxZoom ?? 14;
				
				if (currentZoom >= maxZoom && this.markerClusterer) {
					// At max zoom - disable clustering, show all markers
					this.markerClusterer.clearMarkers();
					this.markers.forEach((marker: unknown) => {
						// Handle both Marker and AdvancedMarkerElement
						if (marker && typeof marker === 'object') {
							const m = marker as Record<string, unknown>;
							if (typeof m.setMap === 'function') {
								(m.setMap as (map: google.maps.Map | null) => void)(this.map);
							} else if ('map' in m) {
								m.map = this.map;
							}
						}
					});
				} else if (currentZoom < maxZoom && this.markerClusterer) {
					// Below max zoom - enable clustering
					this.markerClusterer.addMarkers(this.markers);
				}
			});
		} catch (error) {
			console.error('Error initializing marker clustering:', error);
		}
	}

	/**
	 * Toggle marker clustering on/off.
	 * @param enabled - true to enable clustering, false to disable
	 */
	public setClusteringEnabled(enabled: boolean): void {
		if (!this.markerClusterer) {
			console.warn('Marker clusterer not initialized');
			return;
		}

		try {
			if (enabled) {
				// Re-render clusters
				this.markerClusterer.render();
			} else {
				// Clear clustering by removing all markers from clusterer
				this.markerClusterer.clearMarkers();
				// Re-add markers directly to map (handle both Marker and AdvancedMarkerElement)
				this.markers.forEach((marker: unknown) => {
					if (marker && typeof marker === 'object') {
						const m = marker as Record<string, unknown>;
						if (typeof m.setMap === 'function') {
							(m.setMap as (map: google.maps.Map | null) => void)(this.map);
						} else if ('map' in m) {
							m.map = this.map;
						}
					}
				});
			}
		} catch (error) {
			console.error('Error toggling marker clustering:', error);
		}
	}

	/**
	 * Get the marker clusterer instance.
	 * @returns MarkerClusterer instance or null if not initialized
	 */
	public getMarkerClusterer(): unknown {
		return this.markerClusterer;
	}

	/**
	 * Update cluster options (e.g., grid size, algorithm).
	 * @param options - Partial MarkerClusterer options
	 */
	public updateClusterOptions(options: Record<string, unknown>): void {
		if (!this.markerClusterer) {
			console.warn('Marker clustering not initialized');
			return;
		}

		try {
			// Recreate clusterer with updated options
			this.markerClusterer = new MarkerClusterer({
				map: this.map,
				markers: this.markers,
				...options,
			});
		} catch (error) {
			console.error('Error updating cluster options:', error);
		}
	}
}
